<template>
  <div class="container-fluid">
    <CRow class="pr-2">
      <CCol col="12" xl="10" class="p-0">
        <CCard>
          <CCardBody>
            <a-card class="edit-card" style="margin:0;" :body-style="{padding:0}">
              <span slot="title">
                <h6 class="w-100 mb-0 text-muted">Obchod &gt; <router-link to="/product-sets">Produktové sety</router-link></h6>
                {{ isNew ? 'Nový set' : (set.title || 'Upraviť set') }}
              </span>
              <span slot="extra">
                <a-button class="mr-2" @click="$router.push('/product-sets')">Späť</a-button>
                <a-button type="primary" :loading="saving" @click="save">Uložiť</a-button>
              </span>

              <a-spin v-if="loading" class="d-block text-center p-4" />

              <div v-else class="ps-body">
                <!-- Hlavička setu -->
                <div class="row">
                  <div class="col-md-6">
                    <label class="ps-label">Názov</label>
                    <a-input v-model="set.title" :placeholder="defaultTitle || 'Názov setu'" @input="onTitleInput" />
                    <div v-if="!titleCustom && defaultTitle" class="text-muted" style="font-size:11px; margin-top:2px;">
                      Automaticky z produktov. Vlastný názov zadáte prepísaním.
                    </div>
                    <div v-if="titleCustom" style="font-size:11px; margin-top:2px;">
                      <a href="javascript:;" @click="resetTitle">Použiť automatický názov</a>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="ps-label">Slug (voliteľné)</label>
                    <a-input v-model="set.slug" placeholder="automaticky z názvu" />
                  </div>
                  <div class="col-md-12 mt-2">
                    <label class="ps-label">Popis</label>
                    <a-textarea v-model="set.description" :rows="2" placeholder="Popis setu" />
                  </div>
                  <div class="col-md-6 mt-2">
                    <label class="ps-label">Poradie</label>
                    <a-input-number v-model="set.weight" :min="0" style="width:120px" />
                  </div>
                  <div class="col-md-6 mt-2">
                    <label class="ps-label">Publikované</label><br>
                    <a-switch :checked="set.published == 1" @change="v => set.published = v ? 1 : 0" />
                  </div>
                  <div class="col-md-12 mt-3">
                    <label class="ps-label">Spoločný obrázok setu (voliteľné)</label>
                    <div class="d-flex align-items-center" style="gap:12px;">
                      <div v-if="imagePreview" class="ps-image-preview">
                        <img :src="imagePreview" alt="obrázok setu">
                      </div>
                      <a-upload name="upload" :multiple="false" :showUploadList="false" :customRequest="(o) => uploadSetImage(o)">
                        <a-button :loading="uploadingSet">
                          <i class="fas fa-upload mr-1"></i>{{ image ? 'Zmeniť obrázok' : 'Nahrať obrázok' }}
                        </a-button>
                      </a-upload>
                      <a-button v-if="image" type="danger" @click="removeSetImage">Odstrániť</a-button>
                    </div>
                  </div>
                </div>

                <!-- Farebné varianty -->
                <div class="mt-4">
                  <div class="d-flex align-items-center justify-content-between">
                    <h6 class="mb-0">Farebné varianty setu</h6>
                    <a-button size="small" type="primary" @click="addVariant">+ Pridať variant</a-button>
                  </div>
                  <p class="text-muted" style="font-size:12px; margin-top:4px;">
                    Každý variant je celá zostava produktov (napr. "Biela" = biele zariadenia, "Čierna" = čierne). Zákazník si na webe vyberie farbu setu.
                  </p>

                  <a-alert
                    v-if="!variants.length"
                    type="info"
                    show-icon
                    class="mt-2"
                    message="Set nemá žiadny variant. Pridajte aspoň jeden (napr. 'Biela')."
                  />

                  <a-tabs v-else v-model="activeVariant" type="card" class="mt-2">
                    <a-tab-pane v-for="(variant, vIdx) in variants" :key="String(vIdx)">
                      <template slot="tab">
                        <span v-if="variant.image && (variant.image.styled || variant.image.uri)" class="ps-tab-swatch" :style="{ backgroundImage: 'url(' + (variant.image.styled || variant.image.uri) + ')' }"></span>
                        {{ variant.label || ('Variant ' + (vIdx + 1)) }}
                      </template>

                      <!-- Hlavička variantu -->
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label class="ps-label">Názov variantu (farba)</label>
                          <a-input v-model="variant.label" placeholder="napr. Biela" />
                        </div>
                        <div class="col-md-6">
                          <label class="ps-label">Obrázok variantu (voliteľné)</label>
                          <div class="d-flex align-items-center" style="gap:10px;">
                            <div v-if="variant.image && (variant.image.styled || variant.image.uri)" class="ps-image-preview sm">
                              <img :src="variant.image.styled || variant.image.uri" alt="variant">
                            </div>
                            <a-upload name="upload" :multiple="false" :showUploadList="false" :customRequest="(o) => uploadVariantImage(o, variant)">
                              <a-button size="small" :loading="variant._uploading">
                                <i class="fas fa-upload mr-1"></i>{{ variant.image ? 'Zmeniť' : 'Nahrať' }}
                              </a-button>
                            </a-upload>
                            <a-button v-if="variant.image" size="small" type="danger" @click="variant.image = null">Odstrániť</a-button>
                          </div>
                        </div>
                      </div>

                      <!-- Vyhľadávanie produktov do tohto variantu -->
                      <div class="mb-2">
                        <label class="ps-label">Pridať produkt do variantu</label>
                        <a-input-search
                          v-model="searchTerm"
                          placeholder="Hľadať produkt podľa názvu/kódu"
                          :loading="searching"
                          @search="searchProducts"
                          @change="onSearchInput"
                        />
                        <div v-if="searchResults.length" class="ps-search-results mt-2">
                          <table class="w-100">
                            <tr v-for="p in searchResults" :key="'sr'+p.entity_id" class="ps-search-row">
                              <td style="width:50px;">
                                <img v-if="p.image && p.image.styled" :src="p.image.styled" style="max-height:36px;max-width:36px;">
                                <span v-else class="text-muted">-</span>
                              </td>
                              <td>{{ p.value }}<div v-if="p.code" class="text-muted" style="font-size:11px;">{{ p.code }}</div></td>
                              <td style="width:90px; text-align:right;">{{ formatPrice(p.price) }}</td>
                              <td style="width:90px; text-align:right;">
                                <a-button size="small" :disabled="isAdded(variant, p.entity_id)" @click="addItem(variant, p)">
                                  {{ isAdded(variant, p.entity_id) ? 'Pridané' : '+ Pridať' }}
                                </a-button>
                              </td>
                            </tr>
                          </table>
                        </div>
                      </div>

                      <!-- Položky variantu -->
                      <p v-if="!variant.items.length" class="text-muted">Variant nemá žiadne položky. Vyhľadajte a pridajte produkty.</p>
                      <table v-else class="w-100 ps-items-table">
                        <thead>
                          <tr>
                            <th style="width:50px;"></th>
                            <th>Produkt</th>
                            <th style="width:90px; text-align:center;">Kusov</th>
                            <th style="width:110px; text-align:right;">Cena / ks</th>
                            <th style="width:160px;">Zľava</th>
                            <th style="width:150px; text-align:right;">Po zľave</th>
                            <th style="width:50px;"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(it, idx) in variant.items" :key="'it'+vIdx+'_'+it.node_id">
                            <td>
                              <img v-if="itemImage(it)" :src="itemImage(it)" style="max-height:36px;max-width:36px;">
                              <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                              {{ it.title || ('#' + it.node_id) }}
                              <div v-if="it.code" class="text-muted" style="font-size:11px;">{{ it.code }}</div>
                            </td>
                            <td style="text-align:center;">
                              <a-input-number v-model="it.quantity" size="small" :min="1" :step="1" style="width:70px;" @change="() => normalizeQty(it)" />
                            </td>
                            <td style="text-align:right;">{{ formatPrice(it.base_price) }}</td>
                            <td>
                              <a-select v-model="it.discount_type" size="small" style="width:95px;" @change="() => onDiscountTypeChange(it)">
                                <a-select-option value="none">Žiadna</a-select-option>
                                <a-select-option value="percent">%</a-select-option>
                                <a-select-option value="amount">€</a-select-option>
                              </a-select>
                              <a-input-number
                                v-if="it.discount_type !== 'none'"
                                v-model="it.discount_value"
                                size="small"
                                :min="0"
                                :max="it.discount_type === 'percent' ? 100 : undefined"
                                :step="it.discount_type === 'percent' ? 1 : 0.5"
                                style="width:80px; margin-left:6px;"
                              />
                              <div v-if="discountError(it)" class="text-danger" style="font-size:11px;">{{ discountError(it) }}</div>
                            </td>
                            <td style="text-align:right;">
                              {{ formatPrice(computeFinal(it)) }}
                              <div v-if="(it.quantity || 1) > 1" class="text-muted" style="font-size:11px;">{{ it.quantity }} ks = {{ formatPrice(computeFinal(it) * (it.quantity || 1)) }}</div>
                              <div v-if="computeSaving(it) > 0" class="text-success" style="font-size:11px;">-{{ formatPrice(computeSaving(it) * (it.quantity || 1)) }}</div>
                            </td>
                            <td style="text-align:right;">
                              <a-button size="small" type="danger" @click="removeItem(variant, idx)"><i class="fas fa-times"></i></a-button>
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr class="ps-totals">
                            <td colspan="5" style="text-align:right;">
                              <strong>Spolu variant</strong>
                              <span class="text-muted" style="font-size:11px;"> (základ {{ formatPrice(variantBaseTotal(variant)) }})</span>
                            </td>
                            <td style="text-align:right;">
                              <strong>{{ formatPrice(variantFinalTotal(variant)) }}</strong>
                              <div v-if="variantSavingTotal(variant) > 0" class="text-success" style="font-size:11px;">úspora {{ formatPrice(variantSavingTotal(variant)) }}</div>
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>

                      <div class="text-right mt-3">
                        <a-popconfirm
                          placement="topRight"
                          okText="Áno, odstrániť"
                          cancelText="Zrušiť"
                          @confirm="removeVariant(vIdx)"
                        >
                          <template slot="title">Odstrániť celý variant "{{ variant.label || ('Variant ' + (vIdx + 1)) }}"?</template>
                          <a-button size="small" type="danger"><i class="fas fa-trash mr-1"></i>Odstrániť variant</a-button>
                        </a-popconfirm>
                      </div>
                    </a-tab-pane>
                  </a-tabs>
                </div>
              </div>
            </a-card>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { dataService } from "../_services";
import { getURL } from "../api";
import { authHeader } from "../_helpers";

export default {
  name: "product-set-edit",
  props: ["id"],
  data() {
    return {
      loading: false,
      saving: false,
      url: getURL(),
      set: { id: null, title: '', slug: '', description: '', published: 1, weight: 0 },
      titleCustom: false,
      image: null,
      uploadingSet: false,
      variants: [],
      activeVariant: '0',
      searchTerm: '',
      searching: false,
      searchResults: [],
      searchDebounce: null
    };
  },
  computed: {
    isNew() {
      return !this.id || this.id === 'new' || isNaN(Number(this.id));
    },
    currentVariant() {
      const i = Number(this.activeVariant);
      return this.variants[i] || null;
    },
    // Predvolený názov setu - z prvého variantu (zostava produktov spojená cez +)
    defaultTitle() {
      const v = this.variants[0];
      if (!v || !v.items.length) return '';
      return v.items.map(it => it.title || ('#' + it.node_id)).filter(Boolean).join(' + ');
    },
    imagePreview() {
      if (!this.image) return null;
      return this.image.styled || this.image.uri || null;
    }
  },
  watch: {
    defaultTitle(val) {
      if (!this.titleCustom) this.set.title = val;
    }
  },
  methods: {
    onTitleInput() {
      this.titleCustom = !!(this.set.title && this.set.title.length);
    },
    resetTitle() {
      this.titleCustom = false;
      this.set.title = this.defaultTitle;
    },

    formatPrice(v) {
      if (v == null || isNaN(Number(v))) return '-';
      return Number(v).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    },

    itemImage(it) {
      const img = it && it.image;
      if (!img) return null;
      if (typeof img === 'string') return img;
      return img.styled || img.uri || null;
    },

    computeFinal(it) {
      const base = Number(it.base_price) || 0;
      if (it.discount_type === 'percent') {
        let p = Number(it.discount_value) || 0;
        if (p < 0) p = 0; if (p > 100) p = 100;
        return +(base * (1 - p / 100)).toFixed(2);
      }
      if (it.discount_type === 'amount') {
        let a = Number(it.discount_value) || 0;
        if (a < 0) a = 0;
        return +Math.max(0, base - a).toFixed(2);
      }
      return base;
    },
    computeSaving(it) {
      return +((Number(it.base_price) || 0) - this.computeFinal(it)).toFixed(2);
    },
    discountError(it) {
      if (it.discount_type === 'percent') {
        const p = Number(it.discount_value);
        if (it.discount_value !== '' && it.discount_value != null && (p < 0 || p > 100)) return 'Percento musí byť 0-100';
      }
      if (it.discount_type === 'amount') {
        const a = Number(it.discount_value);
        const base = Number(it.base_price) || 0;
        if (it.discount_value !== '' && it.discount_value != null) {
          if (a <= 0) return 'Suma musí byť > 0';
          if (base && a >= base) return 'Suma musí byť menšia než cena';
        }
      }
      return '';
    },
    onDiscountTypeChange(it) {
      if (it.discount_type === 'none') this.$set(it, 'discount_value', null);
      else if (it.discount_value == null) this.$set(it, 'discount_value', 0);
    },
    normalizeQty(it) {
      let q = Math.floor(Number(it.quantity) || 1);
      if (q < 1) q = 1;
      this.$set(it, 'quantity', q);
    },

    // Súčty za variant (zohľadňujú kusy)
    variantBaseTotal(v) {
      return v.items.reduce((s, it) => s + (Number(it.base_price) || 0) * (Number(it.quantity) || 1), 0);
    },
    variantFinalTotal(v) {
      return v.items.reduce((s, it) => s + this.computeFinal(it) * (Number(it.quantity) || 1), 0);
    },
    variantSavingTotal(v) {
      return v.items.reduce((s, it) => s + this.computeSaving(it) * (Number(it.quantity) || 1), 0);
    },

    // Varianty
    addVariant() {
      this.variants.push({ label: '', image: null, items: [], _uploading: false });
      this.activeVariant = String(this.variants.length - 1);
    },
    removeVariant(idx) {
      this.variants.splice(idx, 1);
      const max = this.variants.length - 1;
      if (Number(this.activeVariant) > max) this.activeVariant = String(Math.max(0, max));
    },

    // Vyhľadávanie
    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      const term = this.searchTerm;
      if (!term || term.length < 2) { this.searchResults = []; return; }
      this.searchDebounce = setTimeout(() => this.searchProducts(term), 400);
    },
    searchProducts(value) {
      const term = value || this.searchTerm;
      if (!term) return;
      this.searching = true;
      dataService.axiosPost('findproducts', { searchterm: term, ct: 'product' }).then(res => {
        this.searching = false;
        const d = res && res.data ? (res.data.data !== undefined ? res.data.data : res.data) : null;
        this.searchResults = Array.isArray(d) ? d : [];
      }).catch(() => {
        this.searching = false;
        this.searchResults = [];
        this.$message.error('Vyhľadávanie zlyhalo.');
      });
    },

    isAdded(variant, nodeId) {
      return variant.items.some(it => String(it.node_id) === String(nodeId));
    },
    addItem(variant, p) {
      if (this.isAdded(variant, p.entity_id)) return;
      variant.items.push({
        node_id: p.entity_id,
        title: p.value,
        code: p.code || '',
        base_price: p.price != null ? Number(p.price) : 0,
        image: p.image || null,
        quantity: 1,
        discount_type: 'none',
        discount_value: null,
        weight: variant.items.length
      });
    },
    removeItem(variant, idx) {
      variant.items.splice(idx, 1);
    },

    // Upload spoločného obrázka setu
    uploadSetImage(opt) {
      this.uploadingSet = true;
      this.doUpload(opt.file).then(img => {
        this.uploadingSet = false;
        if (img) { this.image = img; this.$message.success('Obrázok nahraný.'); }
        else this.$message.error('Nahranie obrázka zlyhalo.');
      }).catch(() => {
        this.uploadingSet = false;
        this.$message.error('Chyba pri nahrávaní obrázka.');
      });
    },
    removeSetImage() { this.image = null; },

    // Upload obrázka variantu
    uploadVariantImage(opt, variant) {
      this.$set(variant, '_uploading', true);
      this.doUpload(opt.file).then(img => {
        this.$set(variant, '_uploading', false);
        if (img) { this.$set(variant, 'image', img); this.$message.success('Obrázok variantu nahraný.'); }
        else this.$message.error('Nahranie obrázka zlyhalo.');
      }).catch(() => {
        this.$set(variant, '_uploading', false);
        this.$message.error('Chyba pri nahrávaní obrázka.');
      });
    },

    // Spoločný upload cez endpoint node image polí
    doUpload(file) {
      const formData = new FormData();
      formData.append('upload', file);
      return this.axios.post(this.url.API_URL + '/upload/content', formData, {
        headers: authHeader(true)
      }).then(res => {
        const u = res && res.data ? res.data[0] : null;
        if (!u || !u.fid) return null;
        return { fid: u.fid, uri: u.uri, relative: u.relative, styled: u.styled || u.uri };
      });
    },

    normalizeImage(img) {
      if (!img) return null;
      if (typeof img === 'string') return { uri: img, styled: img };
      return img;
    },

    fetch() {
      if (this.isNew) {
        // nový set - začni s jedným prázdnym variantom
        this.variants = [{ label: '', image: null, items: [], _uploading: false }];
        this.activeVariant = '0';
        return;
      }
      this.loading = true;
      dataService.axiosFetch('product-set', this.id).then(data => {
        this.loading = false;
        if (!data || data.success === false) {
          this.$message.error((data && data.message) || 'Set sa nepodarilo načítať.');
          return;
        }
        this.set = {
          id: data.id,
          title: data.title || '',
          slug: data.slug || '',
          description: data.description || '',
          published: data.published != null ? Number(data.published) : 1,
          weight: data.weight != null ? Number(data.weight) : 0
        };
        this.image = this.normalizeImage(data.image);

        const mapItem = (it, i) => ({
          node_id: it.node_id,
          title: it.title,
          code: it.code || '',
          base_price: it.base_price != null ? Number(it.base_price) : 0,
          image: this.normalizeImage(it.image),
          quantity: it.quantity != null ? Math.max(1, Number(it.quantity)) : 1,
          discount_type: it.discount_type || 'none',
          discount_value: it.discount_value != null ? Number(it.discount_value) : null,
          weight: it.weight != null ? Number(it.weight) : i
        });

        if (Array.isArray(data.variants) && data.variants.length) {
          this.variants = data.variants.map(v => ({
            label: v.label || '',
            image: this.normalizeImage(v.image),
            items: (v.items || []).map(mapItem),
            _uploading: false
          }));
        } else {
          // spätná kompatibilita: starý set bez variantov -> jeden variant z items
          this.variants = [{
            label: '',
            image: null,
            items: (data.items || []).map(mapItem),
            _uploading: false
          }];
        }
        this.activeVariant = '0';
        this.titleCustom = !!(this.set.title && this.set.title !== this.defaultTitle);
      }).catch(() => {
        this.loading = false;
        this.$message.error('Chyba pri načítaní setu.');
      });
    },

    buildItemRow(it, i) {
      const row = { node_id: it.node_id, quantity: Math.max(1, Number(it.quantity) || 1), weight: i };
      if (it.discount_type === 'percent' || it.discount_type === 'amount') {
        row.discount_type = it.discount_type;
        row.discount_value = Number(it.discount_value) || 0;
      } else {
        row.discount_type = 'none';
      }
      return row;
    },

    save() {
      if (!this.set.title) {
        this.$message.warning('Zadajte názov setu.');
        return;
      }
      if (!this.variants.length) {
        this.$message.warning('Pridajte aspoň jeden variant.');
        return;
      }
      // validácia: každý variant má názov a aspoň jednu položku, zľavy OK
      for (let i = 0; i < this.variants.length; i++) {
        const v = this.variants[i];
        if (!v.label) { this.$message.warning('Zadajte názov pre variant ' + (i + 1) + '.'); return; }
        if (!v.items.length) { this.$message.warning('Variant "' + v.label + '" nemá žiadne položky.'); return; }
        if (v.items.find(it => this.discountError(it))) {
          this.$message.warning('Opravte neplatné zľavy vo variante "' + v.label + '".');
          return;
        }
      }

      this.saving = true;
      const body = {
        title: this.set.title,
        slug: this.set.slug || '',
        description: this.set.description || '',
        published: this.set.published ? 1 : 0,
        weight: Number(this.set.weight) || 0,
        image: this.image ? { fid: this.image.fid, uri: this.image.uri, relative: this.image.relative } : null,
        variants: this.variants.map((v, vi) => ({
          label: v.label,
          weight: vi,
          image: v.image ? { fid: v.image.fid, uri: v.image.uri, relative: v.image.relative } : null,
          items: v.items.map((it, i) => this.buildItemRow(it, i))
        }))
      };
      if (!this.isNew) body.id = Number(this.id);

      dataService.axiosPost('product-set', body).then(res => {
        this.saving = false;
        const d = res && res.data ? (res.data.data !== undefined ? res.data.data : res.data) : null;
        if (!d || d.success === false) {
          this.$message.error((d && d.message) || 'Uloženie zlyhalo.');
          return;
        }
        this.$message.success(d.message || 'Set bol uložený.');
        if (this.isNew && d.id) {
          this.$router.push('/product-set/' + d.id);
        } else {
          this.fetch();
        }
      }).catch(() => {
        this.saving = false;
        this.$message.error('Chyba pri ukladaní setu.');
      });
    }
  },
  mounted() {
    this.fetch();
  }
};
</script>

<style scoped>
.ps-body { padding:16px 20px; }
.ps-image-preview { width:80px; height:80px; border:1px solid #eee; border-radius:6px; overflow:hidden; display:flex; align-items:center; justify-content:center; background:#fafafa; }
.ps-image-preview.sm { width:48px; height:48px; }
.ps-image-preview img { max-width:100%; max-height:100%; }
.ps-label { display:block; font-size:12px; color:#8c8c8c; margin-bottom:2px; margin-top:4px; }
.ps-search-results { border:1px solid #eee; border-radius:6px; max-height:280px; overflow:auto; }
.ps-search-row td { padding:6px 8px; border-bottom:1px solid #f5f5f5; vertical-align:middle; }
.ps-items-table th { font-size:12px; color:#8c8c8c; padding:6px 8px; border-bottom:1px solid #eee; text-align:left; }
.ps-items-table td { padding:6px 8px; border-bottom:1px solid #f5f5f5; vertical-align:middle; }
.ps-totals td { border-top:2px solid #eee; padding-top:8px; }
.ps-tab-swatch { display:inline-block; width:14px; height:14px; border-radius:3px; margin-right:6px; vertical-align:middle; background-size:cover; background-position:center; border:1px solid #ddd; }
.text-success { color:#52c41a; }
.text-danger { color:#f5222d; }
</style>
